import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { contacts } from '@repletoryreach/sdk-api';
import { useT } from '../../../i18n/I18nProvider';
import { ContactTag } from './ContactTag';

/** Shared key, so a bulk tag or a save invalidates every consumer of the vocabulary. */
export const TAGS_KEY = ['contacts', 'tags'] as const;

/**
 * The organisation's tag vocabulary.
 *
 * Tags are how this product groups contacts, so this is the audience list. It
 * comes from `GET /v1/contacts/tags` rather than from the rows on screen: the
 * page used to build its filter from whatever it had fetched, so a tag used
 * only by contacts further down the book was not offered at all, and the count
 * beside each one was not available in any form.
 */
export function useTagVocabulary() {
  const q = useQuery({
    queryKey: TAGS_KEY,
    queryFn: () => contacts.listTags(),
    staleTime: 30_000
  });
  return { tags: q.data?.data ?? [], isPending: q.isPending };
}

/**
 * Tag chips with an input that offers the existing vocabulary.
 *
 * Free text with a datalist rather than a closed select: a new tag has to be
 * creatable from the form that needs it, and offering what already exists is
 * what stops "VIP", "vip" and "V.I.P" becoming three different audiences.
 */
export function TagPicker({
  value, onChange, label, description, id
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  label?: string;
  description?: string;
  id?: string;
}) {
  const t = useT();
  const [draft, setDraft] = useState('');
  const { tags } = useTagVocabulary();
  const listId = `${id ?? 'tags'}-vocabulary`;

  const suggestions = useMemo(
    () => tags.map((x) => x.tag).filter((x) => !value.some((v) => v.toLowerCase() === x.toLowerCase())),
    [tags, value]
  );

  function add(raw: string) {
    const tag = raw.trim();
    if (!tag) return;
    // Case-insensitive, matching what the service does on a bulk tag.
    if (!value.some((v) => v.toLowerCase() === tag.toLowerCase())) onChange([...value, tag]);
    setDraft('');
  }

  return (
    <div className="rr-contacts__tagpicker">
      {label && <span className="rr-contacts__tagpicker-label">{label}</span>}

      <div className="rr-contacts__tagpicker-box">
        {/* The same chip the table draws, so a tag looks like itself wherever
            it appears; a Badge here rendered it as small uppercase text. */}
        {value.map((tag) => (
          <span key={tag} className="rr-contacts__tagpicker-chip">
            <ContactTag tag={tag} />
            <button
              type="button"
              className="rr-contacts__tagpicker-remove"
              aria-label={t('contacts.tags.remove', { tag })}
              onClick={() => onChange(value.filter((v) => v !== tag))}
            >
              ×
            </button>
          </span>
        ))}

        <input
          id={id}
          className="rr-contacts__tagpicker-input"
          list={listId}
          placeholder={t('contacts.tags.add')}
          aria-label={t('contacts.tags.add')}
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(draft); }
            // Backspace on an empty box removes the last chip, which is what
            // every tag input people have used before this one does.
            else if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={() => add(draft)}
        />
        <datalist id={listId}>
          {suggestions.map((tag) => <option key={tag} value={tag} />)}
        </datalist>
      </div>

      {description && <span className="rr-contacts__tagpicker-hint">{description}</span>}
    </div>
  );
}
